import { Link } from "react-router";
import { UserCog, ExternalLink } from "lucide-react";
import { formatCount } from "@shared-core/domain/formatters";
import { Card } from "../../primitives/card";
import { paths } from "@shared-core/routing/routes";

interface AccountAllocationMetricCardProps {
  unallocatedTotal: number;
}

/**
 * Manager-dashboard tile for the CSR account allocation backlog (product
 * brief J4 — "Managers see the 'unallocated accounts' count and its trend,
 * so the backlog is visibly one person's to clear"). Same shape as
 * AlertsMetricCard/RecommendationsMetricCard.
 */
export function AccountAllocationMetricCard({
  unallocatedTotal,
}: AccountAllocationMetricCardProps) {
  const hasBacklog = unallocatedTotal > 0;
  return (
    <Link to={paths.accountAllocation()}>
      <Card className="p-4 hover:bg-muted/50 transition-colors cursor-pointer">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
              <UserCog className="size-4" />
              Accounts without a CSR
            </div>
            <div className="text-3xl font-bold text-foreground">
              {formatCount(unallocatedTotal)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {hasBacklog
                ? "Needs a manager to clear the queue"
                : "Every account has a CSR"}
            </div>
          </div>
          <div
            className={`p-2 rounded-full ${hasBacklog ? "bg-amber-500/10" : "bg-green-500/10"}`}
          >
            <UserCog
              className={`size-5 ${hasBacklog ? "text-amber-600" : "text-green-600"}`}
            />
          </div>
        </div>

        <div className="text-xs text-primary inline-flex items-center gap-1">
          View the reallocation queue
          <ExternalLink className="size-3" />
        </div>
      </Card>
    </Link>
  );
}
